import React from 'react'
import { Box, Typography, Divider, Link } from '@mui/material'
import FlightIcon from '@mui/icons-material/Flight'

interface Route {
    origin: string
    destination: string
    href: string
}

interface TrendingSearchesProps {
    title: string
    leftColumnRoutes: Route[]
    middleColumnRoutes?: Route[]
    rightColumnRoutes: Route[]
}

export const TrendingSearches: React.FC<TrendingSearchesProps> = ({
    title,
    leftColumnRoutes,
    middleColumnRoutes = [],
    rightColumnRoutes
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                px: { xs: 2, md: 3 },
                py: { xs: 1, md: 4 },
                bgcolor: { xs: 'transparent', md: 'background.paper' },
                borderRadius: { xs: 0, md: 3 },
                boxShadow: { xs: 'none', md: '0 2px 8px rgba(0,0,0,0.08)' },
                height: '100%',
                minHeight: '100%',
                width: '100%'
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    mb: { xs: 2, md: 3 },
                    color: 'text.primary',
                    fontSize: { xs: '1.125rem', md: '1.25rem' }
                }}
            >
                {title}
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    gap: { xs: 2, md: 3 }
                }}
            >
                {/* Left Column */}
                <Box sx={{ flex: 1 }}>
                    {leftColumnRoutes.map((route, index) => (
                        <Box key={`left-${route.origin}-${route.destination}-${index}`}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: { xs: 1, md: 1.25 }
                                }}
                            >
                                <FlightIcon
                                    sx={{
                                        fontSize: { xs: '1rem', md: '1.125rem' },
                                        color: 'text.secondary',
                                        flexShrink: 0
                                    }}
                                />
                                <Link
                                    href={route.href}
                                    target="_blank"
                                    sx={{
                                        fontSize: { xs: '0.875rem', md: '0.9375rem' },
                                        color: 'text.secondary',
                                        flex: 1,
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {route.origin} - {route.destination}
                                </Link>
                            </Box>
                            {index < leftColumnRoutes.length - 1 && (
                                <Divider
                                    sx={{
                                        borderColor: 'divider',
                                        borderWidth: '0.5px'
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Middle Column - Desktop only */}
                {middleColumnRoutes.length > 0 && (
                    <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }}>
                        {middleColumnRoutes.map((route, index) => (
                            <Box key={`middle-${route.origin}-${route.destination}-${index}`}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        py: 1.25
                                    }}
                                >
                                    <FlightIcon
                                        sx={{
                                            fontSize: '1.125rem',
                                            color: 'text.secondary',
                                            flexShrink: 0
                                        }}
                                    />
                                    <Link
                                        href={route.href}
                                        target="_blank"
                                        sx={{
                                            fontSize: '0.9375rem',
                                            color: 'text.secondary',
                                            flex: 1,
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        {route.origin} - {route.destination}
                                    </Link>
                                </Box>
                                {index < middleColumnRoutes.length - 1 && (
                                    <Divider
                                        sx={{
                                            borderColor: 'divider',
                                            borderWidth: '0.5px'
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Right Column */}
                <Box sx={{ flex: 1 }}>
                    {rightColumnRoutes.map((route, index) => (
                        <Box key={`right-${route.origin}-${route.destination}-${index}`}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: { xs: 1, md: 1.25 }
                                }}
                            >
                                <FlightIcon
                                    sx={{
                                        fontSize: { xs: '1rem', md: '1.125rem' },
                                        color: 'text.secondary',
                                        flexShrink: 0
                                    }}
                                />
                                <Link
                                    href={route.href}
                                    target="_blank"
                                    sx={{
                                        fontSize: { xs: '0.875rem', md: '0.9375rem' },
                                        color: 'text.secondary',
                                        flex: 1,
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {route.origin} - {route.destination}
                                </Link>
                            </Box>
                            {index < rightColumnRoutes.length - 1 && (
                                <Divider
                                    sx={{
                                        borderColor: 'divider',
                                        borderWidth: '0.5px'
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

export type { TrendingSearchesProps }
